import { useNavigate, useSearch } from '@tanstack/react-router';

import { SupplierGroupTypes } from '../../../../types/SupplierGroupMessage';
import { SupplierSubscriptionPlan } from '../../../../types/SupplierGroupSubscriptionMessage';
import { BasicFilterContainer } from '../../../cdl/Filter/BasicFilterContainer/BasicFilterContainer';
import { FilterMultiSelectShort } from '../../../cdl/Filter/FilterMultiSelectShort/FilterMultiSelectShort';
import { FilterRadioGroup } from '../../../cdl/Filter/FilterRadioGroup/FilterRadioGroup';
import { FilterText } from '../../../cdl/Filter/FilterText/FilterText';
import { isEmptyFilterValue } from '../../../cdl/Filter/helper/isEmptyFilterValue';
import { normalizeFilterValue } from '../../../cdl/Filter/helper/normalizeFilterValue';
import { translate } from '../../../common/helpers/translate.helper';
import { useEnums } from '../../../common/hooks/useEnums';

const supplierGroupFilterKeys = ['searchQuery', 'types', 'plans', 'spotAllowed', 'testCompany'] as const;
type SupplierGroupFilterKey = (typeof supplierGroupFilterKeys)[number];

export const SupplierGroupsFilterPills = () => {
    const { getHumanReadableValue } = useEnums();
    const search = useSearch({ from: '/_app/supplier-groups/' });
    const navigate = useNavigate({ from: '/supplier-groups' });
    const hasActiveFilters = supplierGroupFilterKeys.some((key) => !isEmptyFilterValue(search[key]));

    const setFilter = (key: SupplierGroupFilterKey, value: unknown) => {
        navigate({
            search: (previousSearch) => ({
                ...previousSearch,
                [key]: normalizeFilterValue(value),
                page: 0,
            }),
        });
    };

    const clearFilters = () => {
        navigate({
            search: (previousSearch) => ({
                ...previousSearch,
                ...Object.fromEntries(supplierGroupFilterKeys.map((key) => [key, undefined])),
                page: 0,
            }),
        });
    };

    return (
        <BasicFilterContainer hasActiveFilters={hasActiveFilters} onClear={clearFilters}>
            <FilterText
                label={translate('suppliergroups.filters.name')}
                filterByLabel={translate('suppliergroups.filters.filterByName')}
                value={search.searchQuery}
                onChange={(value) => setFilter('searchQuery', value)}
            />
            <FilterMultiSelectShort<SupplierGroupTypes>
                label={translate('suppliergroups.filters.type')}
                filterByLabel={translate('suppliergroups.filters.filterByType')}
                options={Object.values(SupplierGroupTypes).map((type) => ({
                    label: getHumanReadableValue('supplierGroupType', type),
                    value: type,
                }))}
                values={search.types}
                onChange={(values) => setFilter('types', values)}
            />
            <FilterMultiSelectShort<SupplierSubscriptionPlan>
                label={translate('supplierPlan.plan')}
                filterByLabel={translate('supplierPlan.filterByPlan')}
                options={Object.values(SupplierSubscriptionPlan).map((plan) => ({
                    label: getHumanReadableValue('supplierSubscriptionPlan', plan),
                    value: plan,
                }))}
                values={search.plans}
                onChange={(values) => setFilter('plans', values)}
            />
            <FilterRadioGroup
                label={translate('suppliergroups.filters.spotAllowed')}
                filterByLabel={translate('suppliergroups.filters.filterBySpotAllowed')}
                options={[
                    { label: translate('global.yes'), value: true },
                    { label: translate('global.no'), value: false },
                ]}
                value={search.spotAllowed}
                onChange={(value) => setFilter('spotAllowed', value)}
            />
            <FilterRadioGroup
                label={translate('suppliergroups.filters.test')}
                filterByLabel={translate('suppliergroups.filters.filterByTest')}
                options={[
                    { label: translate('global.yes'), value: true },
                    { label: translate('global.no'), value: false },
                ]}
                value={search.testCompany}
                onChange={(value) => setFilter('testCompany', value)}
            />
        </BasicFilterContainer>
    );
};
